import { fetchCMSData } from "@clients/contentfulClient";
import {
  ListBlockType_listBlock,
  ListBlockType_listBlock_manualPostsCollection_items,
} from "@components/ListBlock/queries/contentfulTypes/ListBlockType";
import {
  ListBlockResultType_pageCollection_items,
  ListBlockResultType_pageCollection_items_content_Eventpage,
  ListBlockResultType_pageCollection_items_content_Newspage,
  ListBlockResultType_pageCollection_items_content_Reportpage,
} from "@components/PageList/queries/contentfulTypes/ListBlockResultType";
import {
  TIMEZONE_STOCKHOLM,
  nowDate,
  startOfDay,
  toDate,
  toDateInStockholmTimeZone,
} from "@utils/dateHelpers";
import { getUrlFromUrlStructureBySlug } from "@utils/pageHelpers/getUrlFromUrlStructureBySlug";
import { isNullOrEmpty } from "@utils/string";
import dayjs from "dayjs";

import { GET_LIST_RESULTS } from "queries/global/getListResults";
import { getPressReleaseListEntries } from "services/PressReleaseListService";
import { ListItem } from "types/ListItem";
import { PressReleaseListEntry } from "types/PressReleaseListEntry";
import { UrlStructure } from "types/UrlStructure";

import {
  getEventStartDate,
  getPagePublishDate,
  getRealContentTypeIds,
  getTags,
  orderListItems,
} from "./listBlockHelpers";

export const getManualPages = (
  manualPages: Array<ListBlockType_listBlock_manualPostsCollection_items>,
  urlStructure: UrlStructure,
): Array<ListItem> => {
  const pages = [] as Array<ListItem>;

  if (manualPages?.length >= 1) {
    manualPages?.map((page) => {
      const url = getUrlFromUrlStructureBySlug(page?.slug, urlStructure) || "";

      //if page has an url add it to the list
      if (!isNullOrEmpty(url) || !isNullOrEmpty(page?.slug)) {
        if (page?.ivTeaserImage?.[0]) {
          page.ivTeaserImage[0].MediaConversions = [];
          page.ivTeaserImage[0].Categories = [];
        }
        return pages.push({
          id: page?.sys?.id,
          heading: page?.teaserTitle ? page?.teaserTitle : page?.content?.heading,
          text: page?.teaserText ? page?.teaserText : page?.content?.preamble,
          linkText: page?.teaserLinkText ? page?.teaserLinkText : "",
          imageUrl: null,
          ivTeaserImage: page?.ivTeaserImage,
          createdDate: page?.sys?.firstPublishedAt,
          publishDate: getPagePublishDate(page),
          hidePublishDate:
            page?.content?.__typename === "Newspage" && page?.content?.hidePublishDate
              ? page?.content?.hidePublishDate
              : false,
          categories: getTags(page),
          url: url,
          slug: page?.slug,
          startDate:
            page?.content?.__typename === "Eventpage"
              ? toDateInStockholmTimeZone(page?.content?.startDate)
              : null,
          endDate:
            page?.content?.__typename === "Eventpage"
              ? toDateInStockholmTimeZone(page?.content?.endDate)
              : null,
          type: page?.content?.__typename,
        });
      }
    });
  }
  return pages;
};

export const getOrderedAutoSelectedPages = async (
  listBlock: ListBlockType_listBlock,
  preview: boolean,
  urlStructure: UrlStructure,
) => {
  // Contentful pages are only needed when the list block includes Contentful content types,
  // e.g. a list with only "Pressmeddelanden" gets its items from afase-api.
  const includesContentfulPages = getRealContentTypeIds(listBlock?.contentTypes).length > 0;
  const includesPressReleases = listBlock?.contentTypes?.some(
    (type) => type === "Pressmeddelanden",
  );

  const [pagesByCategories, pressReleases] = await Promise.all([
    includesContentfulPages ? fetchPagesByCategories(listBlock?.categories, preview) : [],
    includesPressReleases ? getPressReleaseListEntries() : [],
  ]);

  const pages = getFilteredPageList(listBlock, pagesByCategories, pressReleases, urlStructure);

  return orderListItems(pages, listBlock?.sortBy);
};

//returns pages that has tags in Contentful that matches category filter in listblock
const fetchPagesByCategories = async (
  tags: string[],
  preview: boolean,
): Promise<Array<ListBlockResultType_pageCollection_items>> => {
  const pages: Array<ListBlockResultType_pageCollection_items> = [];

  const getAllPages = async (
    limit,
    skip,
    tags,
  ): Promise<Array<ListBlockResultType_pageCollection_items>> => {
    const response = await fetchCMSData(GET_LIST_RESULTS(limit, skip, tags, preview), preview);
    const { data } = response;

    for (const i of data?.pageCollection?.items) {
      pages.push(i);
    }
    if (data?.pageCollection?.total > pages.length) {
      return await getAllPages(limit, pages.length, tags);
    }
    return pages;
  };

  const allPages = await getAllPages(200, 0, tags);
  return allPages as Array<ListBlockResultType_pageCollection_items>;
};

const getFilteredPageList = (
  listBlock: ListBlockType_listBlock,
  pages: any,
  pressReleases: Array<PressReleaseListEntry>,
  urlStructure: UrlStructure,
): Array<ListItem> => {
  let resultList = [] as Array<ListItem>;

  const contentTypesToList = getRealContentTypeIds(listBlock?.contentTypes) as Array<string>;

  let filteredItems = pages?.filter((item) => {
    if (item?.isContainer) {
      return false;
    }

    // if standardpage or fundingpage
    if (contentTypesToList?.includes(item?.content?.__typename?.toLocaleLowerCase())) {
      if (item?.content?.__typename?.toLocaleLowerCase() === "standardpage") {
        const listFundingPages = listBlock?.contentTypes?.some((type) =>
          type.includes("Utlysningar"),
        );
        const listStandardPages = listBlock?.contentTypes?.some((type) =>
          type.includes("Standardsidor"),
        );

        if (listFundingPages && !listStandardPages) {
          return item?.content?.isFundingPage;
        }
        if (!listFundingPages && listStandardPages) {
          return !item?.content?.isFundingPage;
        }
      }

      return true;
    }
  });

  //funding page filter
  if (listBlock?.fundingPageFilter?.length > 0) {
    const now = dayjs();
    const startOfToday = startOfDay(nowDate(), TIMEZONE_STOCKHOLM);

    filteredItems = filteredItems?.filter((item) => {
      if (!item?.content?.isFundingPage) return true;

      return listBlock?.fundingPageFilter.some((filter: string) => {
        const openingDate = dayjs(toDateInStockholmTimeZone(item?.content?.openingDate));
        const closingDate = dayjs(toDateInStockholmTimeZone(item?.content?.closingDate));

        if (closingDate.isBefore(startOfToday)) return false;

        if (filter === "Kommande utlysningar") {
          return openingDate.isAfter(startOfToday);
        }

        if (filter === "Aktuella utlysningar") {
          return openingDate.isBefore(now) && closingDate.isAfter(now);
        }

        return false;
      });
    });
  }

  // eventpage type/category filter
  if (listBlock?.eventTypeFilter?.length > 0) {
    const eventFilters = listBlock?.eventTypeFilter;
    filteredItems = filteredItems?.filter(
      (item: ListBlockResultType_pageCollection_items) =>
        item?.content?.__typename !== "Eventpage" ||
        eventFilters.some(
          (filter: any) =>
            filter ===
            (item?.content as ListBlockResultType_pageCollection_items_content_Eventpage)
              ?.eventCategory,
        ),
    );
  }
  //eventpage event format filter
  if (listBlock?.eventFormatFilter?.length > 0) {
    const eventFormatFilters = listBlock?.eventFormatFilter;
    filteredItems = filteredItems?.filter(
      (item: ListBlockResultType_pageCollection_items) =>
        item?.content?.__typename !== "Eventpage" ||
        eventFormatFilters.some(
          (filter: any) =>
            filter ===
            (item?.content as ListBlockResultType_pageCollection_items_content_Eventpage)
              ?.eventFormat,
        ),
    );
  }

  //eventpage coming events filter
  if (listBlock?.eventListType === "Kommande evenemang") {
    const now = nowDate();
    filteredItems = filteredItems?.filter((item) => {
      if (item?.content?.__typename !== "Eventpage") {
        return true;
      }

      const start = toDateInStockholmTimeZone(item.content.startDate);
      const end = toDateInStockholmTimeZone(item.content.endDate);

      // keep eventpage that start in the future or are ongoing
      return start >= now || (start < now && end >= now);
    });
  }

  //eventpage completed events filter
  if (listBlock?.eventListType === "Genomförda evenemang") {
    const now = nowDate();
    filteredItems = filteredItems?.filter((item: ListBlockResultType_pageCollection_items) => {
      if (item?.content?.__typename !== "Eventpage") {
        return true;
      }

      return toDateInStockholmTimeZone(item?.content?.endDate) < now;
    });
  }

  //newspage type filter
  if (listBlock?.newsTypeFilter?.length > 0) {
    const newsFilters = listBlock?.newsTypeFilter;
    filteredItems = filteredItems?.filter(
      (item: ListBlockResultType_pageCollection_items) =>
        item?.content?.__typename !== "Newspage" ||
        newsFilters.some(
          (filter: any) =>
            filter ===
            (item?.content as ListBlockResultType_pageCollection_items_content_Newspage)?.newsType,
        ),
    );
  }

  //reportpage type filter
  if (listBlock?.reportTypeFilter?.length > 0) {
    const reportTypeFilter = listBlock?.reportTypeFilter;
    filteredItems = filteredItems?.filter(
      (item: ListBlockResultType_pageCollection_items) =>
        item?.content?.__typename !== "Reportpage" ||
        reportTypeFilter.some(
          (filter: any) =>
            filter ===
            (item?.content as ListBlockResultType_pageCollection_items_content_Reportpage)
              ?.reportType,
        ),
    );
  }

  //year (publish date) filter
  if (listBlock?.yearFilter?.length > 0) {
    const yearFilter = listBlock?.yearFilter;

    filteredItems = filteredItems?.filter((item: ListBlockResultType_pageCollection_items) =>
      yearFilter?.some((filter: any) =>
        item?.content?.__typename === "Eventpage"
          ? filter === getEventStartDate(item)?.getFullYear()?.toString()
          : filter === getPagePublishDate(item)?.getFullYear()?.toString(),
      ),
    );
  }

  //press release filter
  if (pressReleases?.length > 0) {
    const filteredPressreleases = getFilteredPressreleases(listBlock, pressReleases);
    resultList = resultList.concat(filteredPressreleases);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  filteredItems?.map((page) => {
    const url = getUrlFromUrlStructureBySlug(page?.slug, urlStructure) || "";

    //add page to list if url exists
    if (!isNullOrEmpty(url) || !isNullOrEmpty(page?.slug)) {
      if (page?.ivTeaserImage?.[0]) {
        page.ivTeaserImage[0].MediaConversions = [];
        page.ivTeaserImage[0].Categories = [];
      }

      const val = {
        id: page?.sys?.id,
        heading: page?.teaserTitle ? page?.teaserTitle : page?.content?.heading,
        text: page?.teaserText ? page?.teaserText : page?.content?.preamble,
        linkText: page?.teaserLinkText ? page?.teaserLinkText : "",
        imageUrl: null,
        imageAltText: null,
        ivTeaserImage: page?.ivTeaserImage,
        createdDate: page?.sys?.firstPublishedAt && toDate(page?.sys?.firstPublishedAt),
        hidePublishDate:
          page?.content?.__typename === "Newspage" && page?.content?.hidePublishDate
            ? page?.content?.hidePublishDate
            : false,
        publishDate: getPagePublishDate(page),
        url: url,
        slug: page?.slug,
        categories: getTags(page),
        startDate:
          page?.content?.__typename === "Eventpage"
            ? toDateInStockholmTimeZone(page?.content?.startDate)
            : null,
        endDate:
          page?.content?.__typename === "Eventpage"
            ? toDateInStockholmTimeZone(page?.content?.endDate)
            : null,
        type: page?.content?.__typename,
        isFundingPageItem: page?.content?.isFundingPage ?? false,
        fundingOpeningDate: page?.content?.isFundingPage ? page?.content?.openingDate : null,
        fundingClosingDate: page?.content?.isFundingPage ? page?.content?.closingDate : null,
      } as ListItem;
      resultList.push(val);
    }
  });

  return resultList;
};

const getFilteredPressreleases = (
  listBlock: ListBlockType_listBlock,
  pressReleases: Array<PressReleaseListEntry>,
): Array<ListItem> => {
  //you can only add year filter on press releases (entries without slug are already excluded)
  return pressReleases
    .filter((pressRelease) => {
      if (listBlock?.yearFilter?.length > 0) {
        return listBlock.yearFilter.some(
          (filter: any) => filter === toDate(pressRelease.publishDate)?.getFullYear()?.toString(),
        );
      }
      return true;
    })
    .map((pressRelease) => ({
      id: pressRelease.id,
      heading: pressRelease.heading,
      text: pressRelease.text,
      linkText: "",
      imageUrl: pressRelease.imageUrl,
      imageAltText: pressRelease.imageAltText,
      ivMainImage: null,
      ivTeaserImage: null,
      createdDate: toDate(pressRelease.publishDate),
      publishDate: toDate(pressRelease.publishDate),
      url: `/om-oss/pressrum/${pressRelease.slug}`,
      hidePublishDate: false,
      slug: pressRelease.slug,
      categories: ["Pressmeddelande"],
      startDate: null,
      endDate: null,
      type: "pressrelease",
    }));
};
